import type { UpdateContactInput } from 'app/generated/gql/graphql'
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { useLocation, useSearchParams } from 'react-router-dom'
import { compact, startCase } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { ACTION_CENTER_FLAG } from 'app/components/action-center/systemPipelines'
import { useInitialTableSort } from 'app/hooks/useInitialTableSort'
import { useListCopy } from 'app/hooks/useListCopy'
import { useListTraversal } from 'app/hooks/useListTraversal'
import { readLocationIds } from 'app/components/action-center/locationParam'
import { ListMemberStageCell } from 'app/components/lists/board/ListItemStageCell'
import type { List } from 'app/generated/graphql'
import { QuickCreateContactModal } from 'app/components/contacts/quick-create/QuickCreateContactModal'
import { useStickyLocationDefault } from 'app/hooks/useStickyLocation'
import { showModal } from 'app/lib/modal'
import { ContactJson } from 'app/types/entities'
import type { SystemListBand, SystemListDef } from 'app/types/navigation'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import type { SearchResponse } from 'app/services/search'
import { defaultColumns, filterableColumns, hygieneRecallColumns } from 'app/table-columns/contacts'
import {
  convertUrlFiltersToES,
  decodeFilters,
  encodeFilters,
  FilterObject,
  filtersByAttribute,
  PredefinedFilter,
} from 'app/filter.utils'
import { HYGIENE_DUE_SOON_TAB, HYGIENE_OVERDUE_TAB, hygieneTabFilters, type HygieneTab } from 'app/lib/hygieneTabs'
import { useHandleSearchParams, useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { contactNameTerms } from 'lib/contactSearch'
import { isSMCOrg } from 'lib/isSMCOrg'
import { debug, logError, logWarn } from 'lib/logging'
import { useContactStore, useCustomPageStore, useFeatureToggleStore } from 'app/stores/StoreContext'
import {
  useCustomPageCreated,
  useCustomPageDeleted,
  consumeLastCreatedPage,
  isDeletedPageId,
  isPendingPageId,
  acknowledgePendingPageId,
} from 'lib/customPageEvents'
import { familySearchTerms } from 'lib/systemLists'

const CONTACTS_PAGE_SIZE = 100

export function defaultSortForTab(tab: string): string {
  // Overdue lists lead with the most recent hygiene visit; the coming-due list
  // leads with the oldest, since that is the patient who comes due first.
  return tab === `all`
    ? `lastActivityDate:desc`
    : tab === HYGIENE_OVERDUE_TAB
      ? `lastHygieneDate:desc`
      : tab === HYGIENE_DUE_SOON_TAB
        ? `lastHygieneDate:asc`
        : `becameLeadDate:desc`
}

export function toContactSortInput(sort: string | undefined): { field: string; direction: 'ASC' | 'DESC' } | undefined {
  const [field, direction, missing] = sort?.split(`:`) ?? []
  const sortField = field && missing ? `${field}:${direction ?? `asc`}:${missing}` : field
  return sortField ? { field: sortField, direction: (direction?.toUpperCase() as 'ASC' | 'DESC') ?? 'DESC' } : undefined
}

export type UseContactsPageReturn = {
  data: any
  user: ReturnType<typeof useUser>
  isSMC: boolean
  workspace: ReturnType<typeof useWorkspace>
  contacts: SearchResponse<ContactJson> | undefined
  contactsTotalItems: number | undefined
  focusedContact: ContactJson | undefined
  isSwitchingContact: boolean
  openContactDrawer: boolean
  drawerTab: string | undefined
  memoizedFilters: ReturnType<typeof filterableColumns>
  columns: ReturnType<typeof defaultColumns>
  initialSort: ReturnType<typeof useInitialTableSort>
  contactTraversal: ReturnType<typeof useListTraversal<ContactJson>>
  contactFetcher: { data: ContactJson | undefined; state: string }
  baseUrl: string
  contactHandler: (contactId: string) => void
  closeDrawerHandler: () => void
  loadContact: (contact?: ContactJson) => void
  addContact: (contact: ContactJson) => void
  loadMore: () => Promise<unknown>
  searchTerms: string
}

export function useContactsPage(
  options: {
    list?: SystemListDef
    band?: SystemListBand
    /** A manual list read through the List API: the table shows its members and a Stage column over its stages. */
    listMembers?: { list: List }
    /** A filter list's rule, from the List API. It defines the table without a `?tab=` lookup. */
    filterRule?: { id: string; name: string; filters: FilterObject[]; join: string }
  } = {},
): UseContactsPageReturn {
  const { list, band, listMembers, filterRule } = options
  const user = useUser()
  const isSMC = isSMCOrg(user?.orgId!)
  const isSuper = user?.type === `super`
  const needsActionDateEnabled = useFeatureToggle(`needs-action-date`)
  const actionCenterEnabled = useFeatureToggle(ACTION_CENTER_FLAG)
  const copy = useListCopy()
  const checkPermission = useCheckPermission()
  const isTasksEnabled = user?.workspaceId != null
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const location = useLocation()
  const baseUrl = `${location.pathname}${location.search.replace(/(\?|&)drawer=(\w+)/, ``)}`
  const [focusedContact, setFocusedContact] = useState<ContactJson | undefined>()
  const [isSwitchingContact, setIsSwitchingContact] = useState(false)
  const [openContactDrawer, setOpenContactDrawer] = useState<boolean>(
    () => searchParams.has(`drawer`) && searchParams.get(`drawer`) !== `new`,
  )
  const [contactFetcherData, setContactFetcherData] = useState<ContactJson>()
  const contactFetcher = { data: contactFetcherData, state: `idle` }
  const workspace = useWorkspace()
  const contactStore = useContactStore()
  const featureToggleStore = useFeatureToggleStore()
  const customPageStore = useCustomPageStore()
  const [contactsTotalItems, setContactsTotalItems] = useState<number | undefined>()
  const contactRequestIdRef = useRef(0)
  const prevSearchKeyRef = useRef<string>(``)

  // A manual list reads as `all`: every member shows, sorted by name. Falling
  // through to the `active-leads` default would hide members who are not leads.
  const tab = list
    ? list.slug
    : listMembers
      ? `all`
      : filterRule
        ? filterRule.id
        : searchParams.get(`tab`) || `active-leads`
  const listSort = (band?.queue ?? list?.queue)?.sort({ needsActionDateEnabled: !!needsActionDateEnabled })
  const defaultSort = listSort ? `${listSort.field}:${listSort.direction.toLowerCase()}` : defaultSortForTab(tab)
  const sort = searchParams.get(`sort`) === `` ? undefined : (searchParams.get(`sort`) ?? defaultSort)
  const page = Number(searchParams.get(`page`) || 0)
  const query = searchParams.get(`query`) ?? ``
  const join = searchParams.get(`join`)
  const limit = (Number(searchParams.get(`limit`)) as 0 | 1 | 100 | 1000) || undefined
  // The list is virtualized and infinite scroll pages through `fetchNextPage`, so
  // a page only needs to cover the visible rows. Without an explicit limit the
  // API returns its maximum of 1000 rows (~2 MB) per request.
  const pageSize = limit ?? CONTACTS_PAGE_SIZE

  // Build filters client-side based on tab
  const [subpages, setSubpages] = useState<Array<{ id: string; name: string; filters: FilterObject[]; join: string }>>(
    [],
  )

  useEffect(() => {
    customPageStore
      .fetchList({ search: `type:contacts` })
      .then((result) => {
        const hits = ((result.data as any)?.customPages?.hits ?? []).filter((h: any) => !isDeletedPageId(h.id))
        for (const h of hits) {
          if (h.id) acknowledgePendingPageId(h.id)
        }
        setSubpages((prev) => {
          const hitIds = new Set(hits.map((h: any) => h.id))
          const optimistic = prev.filter((p) => !hitIds.has(p.id) && isPendingPageId(p.id))
          return [...hits, ...optimistic]
        })
      })
      .catch((err) => logError(`Error fetching custom pages`, err))
  }, [])

  // On mount, check for a page created just before navigation
  useEffect(() => {
    const pending = consumeLastCreatedPage()
    if (pending && pending.page === `contacts` && pending.id && pending.name) {
      setSubpages((prev) => {
        if (prev.some((p) => p.id === pending.id)) return prev
        const filters = typeof pending.filters === `string` ? JSON.parse(pending.filters) : (pending.filters ?? [])
        return [...prev, { id: pending.id!, name: pending.name!, filters, join: pending.join ?? `and` }]
      })
    }
  }, [])

  // Drop a page deleted here or in another tab right away.
  useCustomPageDeleted((id) => setSubpages((prev) => prev.filter((p) => p.id !== id)))

  // Optimistically insert a newly created custom page into the local subpages list
  useCustomPageCreated((newPage) => {
    if (newPage.page === `contacts` && newPage.id && newPage.name) {
      setSubpages((prev) => {
        if (prev.some((p) => p.id === newPage.id)) return prev
        const filters = typeof newPage.filters === `string` ? JSON.parse(newPage.filters) : (newPage.filters ?? [])
        return [...prev, { id: newPage.id!, name: newPage.name!, filters, join: newPage.join ?? `and` }]
      })
    }
  })

  // A filter list's page hands its rule in, so it is known on the first render rather than once
  // the `subpages` read lands.
  const subpage = filterRule ?? subpages?.find((s) => s.id === tab)
  const stickyLocation = useStickyLocationDefault()
  // `readStickyLocation` returns a new array on every render; key on its content.
  const stickyLocationKey = stickyLocation.join(`,`)
  // Keep `filters` referentially stable. It feeds `filterableColumns`, and a new
  // column list makes TableFilters re-run its URL/default sync, which replaces
  // the draft rows of an open Filters panel: any re-render of this page used to
  // drop a rule the user was still editing.
  const { filters, joiner } = useMemo(() => {
    let filters: PredefinedFilter = {}
    let joiner: string | undefined
    if (!list) {
      switch (tab) {
        case `active-leads`:
          filters = needsActionDateEnabled
            ? {
                [`leadStatus.keyword`]: { value: [`Needs Action`, `In Progress`], operator: `in` },
                needsActionDate: { value: `last_30_days`, operator: `between` },
                patientStatus: { value: [`NonPatient`], operator: `not_in` },
              }
            : {
                [`leadStatus.keyword`]: { value: [`Needs Action`, `In Progress`], operator: `in` },
                becameLeadDate: { value: `last_30_days`, operator: `between` },
                patientStatus: { value: [`NonPatient`], operator: `not_in` },
              }
          break
        case `scheduled-leads`:
          filters = {
            [`leadStatus.keyword`]: { value: [`Scheduled`], operator: `in` },
            [`nearestAppointmentDate`]: {
              value: new Date().toISOString().split(`T`)[0],
              operator: `greater_equal_than`,
            },
          }
          break
        case `closed-lost-leads`:
          filters = {
            [`leadStatus.keyword`]: { value: [`Closed Lost`], operator: `in` },
            closedLostReason: { value: [], operator: `in` },
          }
          break
        case HYGIENE_OVERDUE_TAB:
        case HYGIENE_DUE_SOON_TAB:
          filters = hygieneTabFilters(tab as HygieneTab)
          break
        case `all`:
          break
        default:
          if (subpage) {
            joiner = subpage.join
            filters = filtersByAttribute(subpage.filters)
          }
          break
      }
    }
    // The per-device location rides along as one more tab default, so the chip
    // shows and any later filter edit writes it into the URL with the rest.
    if (stickyLocationKey) {
      filters = { ...filters, locationId: { value: stickyLocationKey.split(`,`), operator: `in` } }
    }
    return { filters, joiner }
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
  }, [list, tab, subpage, needsActionDateEnabled, stickyLocationKey])

  const data = {
    slug: tab,
    filters,
    initialFilters: subpage?.filters,
    subpages,
    joiner,
    activeSubpage: list
      ? list.name
      : listMembers
        ? listMembers.list.name
        : // A list's own page is titled by the name its owner typed, never start-cased.
          (filterRule?.name ?? startCase(subpage?.name ?? tab)),
    sort,
    needsActionDateEnabled,
  }

  const addMarketingFilters = isSuper || checkPermission(`org:marketing:advanced-filters`)
  const initialSort = useInitialTableSort(sort)
  const isNotOrgAdmin = !checkPermission([`org:settings:manage-users`])

  const memoizedFilters = useMemo(
    () =>
      filterableColumns({
        isNeedsActionDateEnabled: needsActionDateEnabled,
        isOrgView: !workspace,
        preAppliedFilters: filters,
        type:
          (decodeFilters(searchParams.get(`filters`)).find((filter) => filter.attribute === `platform`)
            ?.value as string) ?? ``,
        isSMC,
        isSuper,
        slug: tab,
        isWokspaceUser: isNotOrgAdmin,
        checkPermission,
        hygieneEnabled: true,
        actionCenterEnabled,
        copy,
      }),
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
    [addMarketingFilters, workspace, filters, tab, needsActionDateEnabled, isNotOrgAdmin, actionCenterEnabled, copy],
  )

  // Fetch contacts via store
  const filtersParam = searchParams.get(`filters`)
  const searchTerms = useMemo(() => {
    const listTerms = [
      ...(list ? familySearchTerms(list, band, { needsActionDateEnabled: !!needsActionDateEnabled }) : []),
    ]
    const predefinedFilterTerms = encodeFilters(
      Object.entries(filters).map(([attribute, f]) => ({ attribute, ...f })) as FilterObject[],
    )
    // Filters in the URL carry their own Match any / Match all; only a saved list's joiner covers the rest.
    const urlJoiner = join === `and` || join === `or` ? join : joiner
    const effectiveFilterTerms = filtersParam
      ? convertUrlFiltersToES(filtersParam, urlJoiner)
      : convertUrlFiltersToES(predefinedFilterTerms, joiner)
    return compact([...listTerms, ...contactNameTerms(query), ...effectiveFilterTerms]).join(` `)
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
  }, [list, band, needsActionDateEnabled, query, filtersParam, filters, joiner, join])

  // The first list request waits for the inputs that shape its filters: the
  // feature toggles pick the active-leads date field, and the sticky location
  // needs the user's workspace. Fetching before that ran an unfiltered org-wide
  // query whose result the next render threw away.
  const filtersReady = featureToggleStore.loadSettled && user != null
  // Key the fetch on the values that change the request. Depending on the whole
  // query string refetched the list every time the drawer param changed.
  const memberListId = listMembers?.list.id
  const contactListKey = `${tab}|${memberListId ?? ``}|${page}|${query}|${filtersParam ?? ``}|${pageSize}|${sort ?? ``}|${searchTerms}`

  useEffect(() => {
    if (!filtersReady) return
    const requestId = contactRequestIdRef.current + 1
    contactRequestIdRef.current = requestId
    // Only reset/update the total when search criteria change (sort doesn't affect count)
    const searchKey = `${tab}|${memberListId ?? ``}|${query}|${filtersParam ?? ``}|${pageSize}|${searchTerms}`
    const searchCriteriaChanged = searchKey !== prevSearchKeyRef.current
    if (searchCriteriaChanged) {
      // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
      contactStore.list = undefined
      setContactsTotalItems(undefined)
      prevSearchKeyRef.current = searchKey
    }
    if (memberListId) {
      // Only a sort the user picked goes along: the tab's default sort names contact fields the List API's default order replaces.
      contactStore
        .fetchListMembers({
          listId: memberListId,
          search: searchTerms || undefined,
          sort: searchParams.get(`sort`) ? toContactSortInput(sort) : undefined,
          pagination: { page, limit: pageSize },
        })
        .then(() => {
          if (requestId !== contactRequestIdRef.current) return
          if (!searchCriteriaChanged && contactsTotalItems != null) return
          setContactsTotalItems(contactStore.listError ? undefined : contactStore.total)
        })
      return
    }
    contactStore
      .fetchList({
        search: searchTerms || undefined,
        pagination: { page, limit: pageSize },
        sort: toContactSortInput(sort),
      })
      .then((result) => {
        if (requestId !== contactRequestIdRef.current) return
        if (!searchCriteriaChanged && contactsTotalItems != null) return
        const total = (result.data as { contacts?: { total?: number } } | undefined)?.contacts?.total
        setContactsTotalItems(result.error ? undefined : total)
      })
  }, [contactListKey, filtersReady])

  useEffect(() => {
    if (focusedContact) setOpenContactDrawer(true)
  }, [focusedContact])

  // `?drawerTab=inbox` lands a deep-linked drawer on that tab (SMS Ops links here). Read once,
  // because the drawer param is rewritten while the contact loads.
  const [drawerTab, setDrawerTab] = useState(() => searchParams.get(`drawerTab`) ?? undefined)

  // Last drawer id written in-page, so the URL effect below skips ids this hook already loaded.
  const drawerIdWrittenRef = useRef<string | null>(null)

  const setDrawerId = (id: string | null) => {
    drawerIdWrittenRef.current = id
    // Route through useHandleSearchParams: it reads the live URL, merges only the
    // changed key, and skips the write when nothing changed. This avoids the timing
    // issues seen with raw setSearchParams (stale RR location) and keeps the drawer
    // param in RR's location state so it survives concurrent sort/filter syncs.
    handleSearchParams({ drawer: id ?? undefined })
  }

  const contactHandler = (contactId: string) => {
    if (contactId === `new`) {
      setFocusedContact(undefined)
      setDrawerId(contactId)
      setOpenContactDrawer(false)
      openCreateContact()
    } else {
      if (focusedContact?.id === contactId) return
      // The open contact stays up, dimmed, until this one loads.
      setIsSwitchingContact(true)
      setDrawerId(contactId)
      setOpenContactDrawer(true)
      contactStore
        .fetchOne(contactId)
        .then((result) => {
          // A later pick or a close owns the drawer now.
          if (drawerIdWrittenRef.current !== contactId) return
          setIsSwitchingContact(false)
          const data = contactStore.current ?? ((result.data as any)?.contact as ContactJson | undefined)
          if (data?.id) {
            setFocusedContact(data)
            setContactFetcherData(data)
            setDrawerId(data.id)
          } else {
            setFocusedContact(undefined)
            setDrawerId(null)
          }
        })
        .catch((err) => {
          setIsSwitchingContact(false)
          logError(`Error fetching contact`, err)
        })
    }
  }

  const closeDrawerHandler = () => {
    setFocusedContact(undefined)
    setIsSwitchingContact(false)
    setOpenContactDrawer(false)
    setDrawerTab(undefined)
    drawerIdWrittenRef.current = null
    handleSearchParams({ drawer: undefined, drawerTab: undefined })
  }

  const closeDrawerOnInlineEdit = () => {
    if (openContactDrawer) closeDrawerHandler()
  }

  const updateContact = async (contact: UpdateContactInput & { id?: string }) => {
    const { id, ...body } = contact
    if (!id) return
    const result = await contactStore.update(id, body)
    if (result.error) {
      logWarn(`Error updating contact`, result.error.message)
      notifyError(`Error updating contact`)
      return
    } else if (result.data) {
      notifySuccess(`Contact updated`)
    }
  }

  const contactColumns = useMemo(
    () =>
      defaultColumns(
        copy,
        contactHandler,
        updateContact,
        closeDrawerOnInlineEdit,
        checkPermission,
        isSMC,
        isSuper,
        isTasksEnabled,
        needsActionDateEnabled,
        tab,
        baseUrl,
        listMembers
          ? [
              {
                id: `listStage`,
                header: `Stage`,
                enableSorting: false,
                meta: {
                  group: `Contact Information`,
                  defaultVisible: true,
                  ControlledComponent: (row) => (
                    <ListMemberStageCell contact={row as ContactJson} list={listMembers.list} />
                  ),
                },
              },
            ]
          : [],
      ),
    [focusedContact?.id, contactStore.list?.total, tab, baseUrl, listMembers?.list, copy],
  )
  const columns = useMemo(
    () => (list?.listKind === `hygiene` ? hygieneRecallColumns(contactColumns) : contactColumns),
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
    [list?.listKind, contactColumns],
  )

  function loadContact(contact?: ContactJson) {
    const contactId = searchParams.get(`contactId`) || searchParams.get(`drawer`) || ``
    debug([`fu`, `pipelines`], `loadContact`, contact, contactId)
    if (focusedContact?.id !== contactId) setFocusedContact(undefined)
    bg(async () => {
      if (!contactId) {
        setOpenContactDrawer(false)
        return
      }
      if (contactId === `new`) {
        setOpenContactDrawer(false)
        openCreateContact()
        return
      }
      // network-only so deep links bypass any stale/empty graphcache entry from a
      // prior fetch in the wrong workspace/org context.
      const loadResult = await contactStore.fetchOne(contactId, { requestPolicy: `network-only` })
      const loadedContact = (loadResult.data as any)?.contact as ContactJson | undefined
      if (!loadedContact) {
        const isServerError = loadResult.error?.graphQLErrors?.some(
          (e) => e.extensions?.code === `INTERNAL_SERVER_ERROR`,
        )
        notifyError(isServerError ? `Error loading contact. Please try again.` : `Contact not found`)
        setDrawerId(null)
        setOpenContactDrawer(false)
        return
      }
      contact = contactStore.current ?? loadedContact
      setFocusedContact(contact === focusedContact ? { ...contact } : contact)
      setOpenContactDrawer(true)
      // Re-assert the drawer param: a workspace switch + reload or a concurrent
      // search-param update (sort/filter sync) can clobber it via React Router's
      // internal location while the contact is still loading. Keep the deep link
      // shareable by stamping the id back once the contact resolves.
      setDrawerId(contact.id!)
    })
  }

  // Route through contactHandler, not loadContact. loadContact resolves the id from
  // React Router's searchParams, which still reads `drawer=new` at this point, so it
  // took the "new" branch and re-opened the create drawer over the contact that had
  // just been created. contactHandler takes the id directly and writes the drawer
  // param through handleSearchParams, which React Router does observe.
  function addContact(contact: ContactJson) {
    if (!contact.id) return
    contactHandler(contact.id)
  }

  /**
   * `drawer=new` stays in the URL while the modal is open so the deep link and
   * the back button keep working. Closing clears it, unless a contact drawer has
   * already claimed the param (the create path opens the new record right away).
   */
  function openCreateContact() {
    // The page's location: the URL filter when there is one, else the sticky
    // location the page fell back to (see the predefined filters above).
    const pageLocationIds = searchParams.has(`filters`) ? readLocationIds(searchParams.get(`filters`)) : stickyLocation
    const [onlyLocationId, ...otherLocationIds] = pageLocationIds
    showModal(
      <QuickCreateContactModal
        onCreate={addContact}
        onExistingContact={(contact) => contact.id && contactHandler(contact.id)}
        defaultLocationId={otherLocationIds.length === 0 ? onlyLocationId : undefined}
      />,
      {
        hideTitle: true,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
        onClose: () => {
          if (new URL(window.location.toString()).searchParams.get(`drawer`) === `new`) setDrawerId(null)
        },
      },
    )
  }

  const contacts = contactStore.list as SearchResponse<ContactJson> | undefined

  const contactTraversal = useListTraversal<ContactJson>(
    (contacts?.hits as ContactJson[]) ?? [],
    focusedContact,
    (contact) => {
      // Pass the shared interned instance (contacts.hits[i]), not a spread copy: the drawer
      // renders from focusedContact, so a copy would detach it from the identity map and a
      // tag/field edit would not reflect until a refetch (TRK-6046).
      setFocusedContact(contact)
      setDrawerId(contact.id!)
    },
  )

  const drawerParam = searchParams.get(`contactId`) || searchParams.get(`drawer`) || ``

  // Opens the drawer for a `?drawer=` id that arrived from outside this page, on mount or later.
  useEffect(() => {
    if (!drawerParam) return
    if (drawerParam === drawerIdWrittenRef.current) return
    if (focusedContact?.id === drawerParam) return
    loadContact()
  }, [drawerParam])

  return {
    data,
    user,
    isSMC,
    workspace,
    contacts,
    // A Remove re-reads the members outside the fetch effect, so a list's table follows the store's member total.
    contactsTotalItems:
      memberListId && contactsTotalItems != null && !contactStore.listError ? contactStore.total : contactsTotalItems,
    focusedContact,
    isSwitchingContact,
    openContactDrawer,
    drawerTab,
    memoizedFilters,
    columns,
    initialSort,
    contactTraversal,
    contactFetcher,
    baseUrl,
    contactHandler,
    closeDrawerHandler,
    loadContact,
    addContact,
    // Scroll-to-load pages through the store, so the drawer's next/previous
    // traversal and bulk selection see the rows the table just grew by.
    loadMore: contactStore.fetchNextPage,
    searchTerms,
  }
}
